import { useMutation } from '@apollo/client/react';
import { useIonRouter, useIonToast } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { DELETE_POST } from '../graphql/mutations/deletePost';
import { GET_CHANNEL_DRAFTS } from '../graphql/queries/channelDrafts';
import { GET_CHANNEL_POSTS } from '../graphql/queries/channelPosts';
import { GET_CHANNEL_TIMELINE } from '../graphql/queries/channelTimeline';
import type {
  DeletePostMutation,
  DeletePostMutationVariables,
} from '../types/graphql-generated';

interface UseDeletePostOptions {
  // User-facing (Spanish) toast copy, so the caller can say "publicación" or
  // "borrador" depending on where the post is being deleted from.
  successMessage: string;
  errorMessage: string;
}

interface DeletePostArgs {
  channelId: string;
  postId: string;
}

// Shared delete-a-post flow used by both the post detail screen and the draft
// editor. Runs the mutation, refetches the owner's lists so the deleted post
// disappears everywhere, and navigates back to the channel replacing the current
// history entry (so back never returns to the now-deleted post).
export const useDeletePost = ({ successMessage, errorMessage }: UseDeletePostOptions) => {
  const ionRouter = useIonRouter();
  const [presentToast] = useIonToast();

  // Bare DocumentNodes refetch the active observers with their current
  // variables — critical for the paginated feed/timeline whose live variables
  // include limit/offset and must not be clobbered.
  // No awaitRefetchQueries: these refetches run in the background to resync
  // pagination/counts. With awaitRefetchQueries a rejected refetch would reject
  // the mutation promise even though the server already deleted the post, so
  // control would jump to the catch and falsely report failure.
  const [deletePost, { loading: deleting }] = useMutation<
    DeletePostMutation,
    DeletePostMutationVariables
  >(DELETE_POST, {
    refetchQueries: [GET_CHANNEL_DRAFTS, GET_CHANNEL_POSTS, GET_CHANNEL_TIMELINE],
  });

  const showToast = (message: string, danger = false) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      cssClass: danger ? 'toast-danger' : 'toast-success',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const confirmDelete = async ({ channelId, postId }: DeletePostArgs) => {
    try {
      const result = await deletePost({
        variables: { input: { channelId, postId } },
        // Evict the deleted post on confirmed success so it disappears from
        // every cached list instantly, without depending on the background
        // refetch succeeding. DELETE_POST returns no post payload, so the
        // identity is built from the known postId + 'Post' typename.
        update: (cache, mutationResult) => {
          if (
            mutationResult.errors?.length ||
            !mutationResult.data?.deletePost?.success
          ) {
            return;
          }
          cache.evict({ id: cache.identify({ __typename: 'Post', id: postId }) });
          cache.gc();
        },
      });
      const payload = result.data?.deletePost;
      if (payload?.errors?.length || !payload?.success) {
        showToast(errorMessage, true);
        return;
      }
      showToast(successMessage);
      ionRouter.push(`/app/main/channels/${channelId}`, 'back', 'replace');
    } catch {
      showToast(errorMessage, true);
    }
  };

  return { confirmDelete, deleting };
};
